import { useEffect } from 'react';

const SAMPLES = [
  {
    id: 'A',
    label: 'Sample A — Equal Moderate Score (Consulting Practice)',
    desc: 'A balanced profile where every diagnostic domain scores around 3.0 / 5.0 in a consulting context. Demonstrates the narrative tone for clustered scores and context-aware future-state workflow (engagement, time capture, client reporting).',
    pages: '14 pages',
    file: '/samples/AWSAI_v4_P1_Equal_Moderate.pdf',
  },
  {
    id: 'B',
    label: 'Sample B — Varied High Score (Manufacturing)',
    desc: 'A differentiated, higher-maturity manufacturing profile. Demonstrates the priority-ranking narrative, workflow cards, and the AI-enabled future-state design for production-line and supplier acceptance.',
    pages: '13 pages',
    file: '/samples/AWSAI_v4_P2_Varied_High.pdf',
  },
  {
    id: 'C',
    label: 'Sample C — Weak Placeholder Names (Procurement)',
    desc: 'Respondent typed placeholder names ("a", "test", blank) with a procurement-themed main problem. Demonstrates context-aware workflow labels and a procurement-specific future-state workflow — no hardcoded academic / PhD fallback.',
    pages: '14 pages',
    file: '/samples/AWSAI_v4_P3_Weak_Names.pdf',
  },
];

const NAVY = '#0B2951';
const TEAL = '#008C85';
const CHARCOAL = '#1F2937';
const GREY = '#6D7586';

const SampleReportsPage = () => {
  useEffect(() => {
    document.title = 'AWSAI · Sample Diagnostic Reports — Aurea Digital Solutions';
  }, []);

  return (
    <div
      data-testid="sample-reports-page"
      style={{
        minHeight: '100vh',
        background: '#F8FAFC',
        color: CHARCOAL,
        fontFamily: 'Helvetica, Arial, sans-serif',
        padding: '64px 24px',
      }}
    >
      <div style={{ maxWidth: 880, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ borderTop: `3px solid ${NAVY}`, paddingTop: 8 }}>
          <div style={{ width: 56, borderTop: `3px solid ${TEAL}`, marginTop: -3, marginBottom: 18 }} />
          <div
            style={{
              fontSize: 11,
              letterSpacing: 3,
              color: NAVY,
              textTransform: 'uppercase',
              fontWeight: 700,
            }}
          >
            Aurea Digital Solutions
          </div>
          <div
            style={{
              fontSize: 10,
              letterSpacing: 2,
              color: GREY,
              textTransform: 'uppercase',
              marginTop: 4,
            }}
          >
            Workflow &amp; AI Diagnostics
          </div>

          <h1
            style={{
              fontSize: 36,
              color: NAVY,
              fontWeight: 800,
              marginTop: 28,
              lineHeight: 1.15,
              letterSpacing: '-0.5px',
            }}
            data-testid="sample-reports-title"
          >
            AWSAI Professional Diagnostic Report
            <br />
            <span style={{ color: TEAL, fontWeight: 700, fontSize: 18 }}>
              Sample Deliverables for Internal Review
            </span>
          </h1>

          <p
            style={{
              fontSize: 14,
              color: CHARCOAL,
              maxWidth: 640,
              marginTop: 14,
              lineHeight: 1.6,
            }}
          >
            The three sample PDFs below illustrate the redesigned, narrative-style Professional
            Diagnostic Report under three different respondent profiles. Each report is 10–14 pages,
            monochrome (navy / charcoal / single teal accent), and reads as a board-ready management
            advisory deliverable. Tables have been replaced by narrative cards; a new AI-Enabled
            Future-State Workflow section is included, and the Recommended Advisory Pathway sets
            out four professional stages for follow-up.
          </p>
          <p
            style={{
              fontSize: 12,
              color: GREY,
              maxWidth: 640,
              marginTop: 10,
              lineHeight: 1.6,
              fontStyle: 'italic',
            }}
          >
            These links are temporary preview deliverables. They are not deployed to production,
            do not require login, and are intended solely for design approval before release.
          </p>
        </div>

        {/* Sample cards */}
        <div style={{ marginTop: 36 }}>
          {SAMPLES.map((s) => (
            <div
              key={s.id}
              data-testid={`sample-card-${s.id}`}
              style={{
                background: '#FFFFFF',
                border: '1px solid #E5E7EB',
                borderLeft: `3px solid ${TEAL}`,
                padding: '22px 26px',
                marginBottom: 16,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
                <span
                  style={{
                    fontSize: 11,
                    color: TEAL,
                    letterSpacing: 2,
                    textTransform: 'uppercase',
                    fontWeight: 700,
                  }}
                >
                  Sample {s.id}
                </span>
                <span style={{ fontSize: 11, color: GREY, letterSpacing: 1 }}>
                  {s.pages}
                </span>
              </div>
              <div
                style={{
                  fontSize: 18,
                  color: NAVY,
                  fontWeight: 700,
                  marginTop: 6,
                }}
              >
                {s.label}
              </div>
              <p
                style={{
                  fontSize: 13,
                  color: CHARCOAL,
                  lineHeight: 1.6,
                  marginTop: 10,
                  marginBottom: 18,
                }}
              >
                {s.desc}
              </p>

              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <a
                  href={s.file}
                  download
                  data-testid={`sample-download-${s.id}`}
                  style={{
                    display: 'inline-block',
                    background: NAVY,
                    color: '#FFFFFF',
                    padding: '11px 22px',
                    fontSize: 13,
                    fontWeight: 700,
                    letterSpacing: 1,
                    textTransform: 'uppercase',
                    textDecoration: 'none',
                    border: `1px solid ${NAVY}`,
                  }}
                >
                  Download Sample {s.id} (PDF)
                </a>
                <a
                  href={s.file}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`sample-open-${s.id}`}
                  style={{
                    display: 'inline-block',
                    background: '#FFFFFF',
                    color: NAVY,
                    padding: '11px 22px',
                    fontSize: 13,
                    fontWeight: 700,
                    letterSpacing: 1,
                    textTransform: 'uppercase',
                    textDecoration: 'none',
                    border: `1px solid ${NAVY}`,
                  }}
                >
                  Open in New Tab
                </a>
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div
          style={{
            borderTop: `1px solid #E5E7EB`,
            marginTop: 36,
            paddingTop: 18,
            fontSize: 12,
            color: GREY,
            lineHeight: 1.7,
          }}
        >
          <div style={{ color: NAVY, fontWeight: 700, fontSize: 11, letterSpacing: 2, textTransform: 'uppercase' }}>
            Reviewer Notes
          </div>
          <ul style={{ paddingLeft: 18, marginTop: 8 }}>
            <li>9-section structure (no Table of Contents).</li>
            <li>Executive Brief strictly one A4 page.</li>
            <li>Strictly monochrome — single teal accent.</li>
            <li>Workflow recommendations are specific, derived from the reported workflow context.</li>
            <li>Scoring logic, admin auth, and public 402 gating remain unchanged.</li>
          </ul>
          <div style={{ marginTop: 14 }}>
            <strong style={{ color: NAVY }}>Aurea Digital Solutions</strong> · Using the AWSAI Framework
            and SMART-Workflow Methodology.
          </div>
        </div>
      </div>
    </div>
  );
};

export default SampleReportsPage;
